import { cn } from '@/lib/utils';
import { fmtMirrorDate, parseMirrorDate } from '@/lib/dates';

/**
 * Shared building blocks for the two account-statement documents — the
 * single-account statement (Statements.tsx) and the merged chain statement
 * (ChainStatement.tsx).
 *
 * Extracted rather than duplicated because these two documents are read side
 * by side by the same accounting person: a store manager checks their own
 * statement against the chain statement corporate received. If the aging
 * label, the badge threshold or the money formatting drifted between them,
 * that comparison would produce a phantom discrepancy.
 */

export type BucketId = 'current' | 'd30' | 'd60' | 'd90' | 'd90plus';

export const BUCKETS: { id: BucketId; label: string }[] = [
  { id: 'current', label: 'Current' },
  { id: 'd30', label: '1–30 days' },
  { id: 'd60', label: '31–60 days' },
  { id: 'd90', label: '61–90 days' },
  { id: 'd90plus', label: '90+ days' },
];

export function bucketFor(days: number): BucketId {
  if (days <= 0) return 'current';
  if (days <= 30) return 'd30';
  if (days <= 60) return 'd60';
  if (days <= 90) return 'd90';
  return 'd90plus';
}

export function agingLabel(days: number): string {
  if (days <= 0) return 'Current';
  return `${days} day${days === 1 ? '' : 's'} past due`;
}

export function fmtDate(iso: string | null): string {
  return fmtMirrorDate(iso ?? '');
}

export function fmtLongDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Days a given open invoice is past due, computed client-side as
 * (today − due date) in whole days. Clamped at 0 (not-yet-due invoices are
 * "Current"). Computed locally rather than trusting a server field so the
 * "As of today" framing on the statement is always internally consistent.
 */
export function daysPastDue(dueIso: string | null, today: Date): number {
  // due_date is a calendar date (YYYY-MM-DD) — anchor it at UTC midnight and
  // compare against today's LOCAL calendar day, also anchored at UTC, so the
  // day count matches what a human reads off both dates.
  const due = parseMirrorDate(dueIso ?? '');
  if (!due) return 0;
  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.floor((todayUtc - due.getTime()) / 86_400_000);
  return days > 0 ? days : 0;
}

export function AgingBadge({ days, className }: { days: number; className?: string }) {
  const overdue = days > 0;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-tight',
        overdue
          ? 'bg-red-50 text-red-600 border border-red-200'
          : 'bg-brix-primary-tint text-brix-primary border border-brix-primary/30',
        className,
      )}
    >
      {agingLabel(days)}
    </span>
  );
}

export function AddressBlock({
  label,
  name,
  lines,
}: {
  label: string;
  name: string;
  lines: string[];
}) {
  return (
    <div>
      <div className="text-[10px] tracking-tight text-slate-900 font-bold">{label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-900 tracking-tight">{name}</div>
      {lines.filter(Boolean).map((l) => (
        <div key={l} className="text-xs text-slate-900 leading-relaxed tracking-tight">
          {l}
        </div>
      ))}
    </div>
  );
}

export function SummaryStat({
  label,
  value,
  emphasis,
  tone,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
  tone?: 'warning';
}) {
  const valueColor = tone === 'warning' ? 'text-red-600' : 'text-slate-900';
  return (
    <div className="rounded-2xl border border-brix-border bg-brix-surface px-4 py-4 text-center shadow-card">
      <div className="text-[10px] tracking-tight text-slate-500 font-semibold">{label}</div>
      <div
        className={cn(
          'mt-1.5 font-mono font-bold tracking-tight leading-none',
          emphasis ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl',
          valueColor,
        )}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </div>
    </div>
  );
}

/** The five aging tiles, shared by both documents. */
export function AgingGrid({ aging }: { aging: Record<BucketId, number> }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
      {BUCKETS.map((b) => {
        const amt = aging[b.id];
        const isOverdue = b.id !== 'current' && amt > 0;
        return (
          <div
            key={b.id}
            className={cn(
              'rounded-xl border px-3 py-3 text-center',
              isOverdue ? 'border-red-200 bg-red-50' : 'border-brix-border bg-brix-surface',
            )}
          >
            <div className="text-[10px] font-semibold tracking-tight text-slate-500">{b.label}</div>
            <div
              className={cn(
                'mt-1 font-mono font-bold text-base tracking-tight',
                isOverdue ? 'text-red-600' : 'text-slate-900',
              )}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {amt.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
